import { i18n } from '@/i18n.js'
import { apiClient, baseURL } from './apiClient.js'
import { useAuthStore } from '@/stores/auth.js'

// --- auth / tenant API ---
async function getTenants() {
  return await apiClient.get('auth/tenants/')
}

async function switchTenant(tenant_slug) {
  return await apiClient.post('auth/tenant/switch/', { tenant_slug })
}

async function getTenantOidc(tenant_slug) {
  return await apiClient.get(`auth/tenant/${tenant_slug}/oidc/`)
}

async function authDiscover(email, tenant_slug = null) {
  const body = { email }
  if (tenant_slug) body.tenant_slug = tenant_slug
  return await apiClient.post('auth/discover/', body)
}

async function authLogin(postData) {
  return await apiClient.post('auth/login/', postData)
}

async function authOidcAuthorize(tenant_slug, provider_id = null) {
  const params = {}
  if (tenant_slug) params.tenant_slug = tenant_slug
  if (provider_id) params.provider_id = provider_id
  return await apiClient.get('auth/oidc/authorize/', params)
}

async function authOidcCallback(code, provider_id, state) {
  const params = { code, state }
  if (provider_id) params.provider_id = provider_id
  return await apiClient.get('auth/oidc/callback/', params)
}

// --- basic app APIs (use apiClient) ---
async function getStatistics() {
  return await apiClient.get('stats/')
}
async function getSystemVersion() {
  return await apiClient.get('system/version/')
}
async function getFrontpage() {
  return await apiClient.get('frontpage/')
}
async function getLocations(params = {}) {
  return await apiClient.get('location/', { page_size: 200, ...params })
}

// tenant role list (used by admin UI)
async function getTenantRoles(tenantSlug, params = {}) {
  return await apiClient.get(`auth/tenant/${tenantSlug}/roles/`, params)
}
async function createLocation(data) {
  return await apiClient.post('location/', data)
}
async function updateLocation(id, data) {
  return await apiClient.patch(`location/${id}/`, data)
}
async function deleteLocation(id) {
  return await apiClient.delete(`location/${id}/`)
}
async function getWarehouse(warehouseId = 0, locationId = 0) {
  const params = {}
  if (locationId) params.location = locationId
  return await apiClient.get(`warehouse/${warehouseId != 0 ? warehouseId + '/' : ''}`, params)
}
async function getCategory(categoryId = 0, params = {}) {
  return categoryId != 0
    ? await apiClient.get(`category/${categoryId}/`)
    : await apiClient.get('category/', params)
}
async function createCategory(data) {
  return await apiClient.post('category/', data)
}
async function updateCategory(id, data) {
  return await apiClient.patch(`category/${id}/`, data)
}
async function deleteCategory(id) {
  return await apiClient.delete(`category/${id}/`)
}
async function getArea(warehouseId = 0) {
  const params = {}
  if (warehouseId) params.warehouse = warehouseId
  return await apiClient.get('area/', params)
}
async function getAreas(params = {}) {
  return await apiClient.get('area/', { page_size: 200, ...params })
}
async function createArea(data) {
  return await apiClient.post('area/', data)
}
async function updateArea(id, data) {
  return await apiClient.patch(`area/${id}/`, data)
}
async function deleteArea(id) {
  return await apiClient.delete(`area/${id}/`)
}
async function getItemMetadata(itemId = 0) {
  return await apiClient.get('item_metadata_extended/', { item: itemId })
}
async function getItemMetadataExtended(warehouse = 0, area = 0, category = 0, extraParams = {}) {
  const params = { page_size: 500, ...extraParams }
  if (warehouse) params.warehouse = warehouse
  if (area) params.area = area
  if (category) params.category = category
  return await apiClient.get('item_metadata_extended/', params)
}
async function getItemMetadataTimeline(id, from = null, to = null) {
  const end = to ? new Date(to) : new Date()
  if (!to) end.setDate(end.getDate() + 120)
  const start = from ? new Date(from) : new Date()
  if (!from) start.setDate(start.getDate() - 30)
  return await apiClient.get(`item_metadata_extended/${id}/timeline/`, {
    from: start.toISOString(),
    to: end.toISOString(),
  })
}
async function getItemFleetTimeline(itemId, from = null, to = null) {
  const end = to ? new Date(to) : new Date()
  if (!to) end.setDate(end.getDate() + 120)
  const start = from ? new Date(from) : new Date()
  if (!from) start.setDate(start.getDate() - 30)
  return await apiClient.get('item_metadata_extended/fleet_availability/', {
    from: start.toISOString(),
    to: end.toISOString(),
    item: itemId,
    include_timeline: true,
  })
}
async function getItems(category = 0, itemId = 0) {
  return itemId != 0
    ? await apiClient.get(`item/${itemId}/`, { category })
    : await apiClient.get('item/', { category })
}
async function getItemById(id) {
  return await apiClient.get(`item/${id}/`)
}

/** Fetch the full item (not metadata) list, e.g. for the browse screen. */
async function getItemsList(params = {}) {
  const res = await apiClient.get('item/', { page_size: 200, ...params })
  return res
}

/**
 * Look up the first item_metadata record for a given item id.
 * Used when adding a Unique or Quantity item to a project from the browse list.
 * Returns the metadata id, or null if none found.
 */
async function getFirstItemMetadataId(itemId) {
  const res = await apiClient.get('item_metadata_extended/', { item: itemId, page_size: 1 })
  const results = res.results ?? res ?? []
  return results[0]?.id ?? null
}
function wikiPageToRef(wiki_page) {
  if (wiki_page == null || wiki_page === '') return null
  if (typeof wiki_page === 'object') return wiki_page
  return { id: wiki_page, type: 'WikiPage' }
}
async function createItem(data) {
  const payload = {
    ...data,
    category: typeof data.category === 'object' && data.category ? data.category.id : data.category,
    wiki_page: wikiPageToRef(data.wiki_page)
  }
  return data.contract_addition_pdf
    ? await apiClient.postForm('item/', itemFormData(payload))
    : await apiClient.post('item/', payload)
}

function itemFormData(payload) {
  const form = new FormData()
  for (const [key, value] of Object.entries(payload)) {
    if (key === 'id') continue
    const fieldValue = value && typeof value === 'object' && !(value instanceof File) ? value.id : value
    form.append(key, fieldValue == null ? '' : fieldValue)
  }
  return form
}

export async function previewItemContractAddition(itemId) {
  return apiClient.getBlob(`item/${itemId}/contract-addition/preview/`)
}
async function deleteItem(id) {
  return await apiClient.delete(`item/${id}/`)
}

async function getItemInstances(itemId) {
  return await apiClient.get('item_metadata_extended/', { item: itemId, page_size: 200 })
}
async function getChildInstances(parentId) {
  return await apiClient.get('item_metadata_extended/', { parent: parentId, page_size: 200 })
}
async function getItemInstance(id) {
  return await apiClient.get(`item_metadata_extended/${id}/`)
}
async function getItemValueSummary({ group_by, as_of_date } = {}) {
  const params = { group_by }
  if (as_of_date) params.as_of_date = as_of_date
  return await apiClient.get('item_metadata_extended/value_summary/', params)
}
async function createItemInstance(data) {
  return await apiClient.post('item_metadata/', data)
}
async function updateItemInstance(id, data) {
  return await apiClient.patch(`item_metadata/${id}/`, data)
}
async function deleteItemInstance(id) {
  return await apiClient.delete(`item_metadata/${id}/`)
}

async function getBundles(params = {}) {
  return await apiClient.get('bundle/', params)
}
async function getBundle(id) {
  return await apiClient.get(`bundle/${id}/`)
}
async function createBundle(data) {
  return await apiClient.post('bundle/', data)
}
async function updateBundle(id, data) {
  return await apiClient.patch(`bundle/${id}/`, data)
}
async function deleteBundle(id) {
  return await apiClient.delete(`bundle/${id}/`)
}

async function getBundleItems(bundleId) {
  return await apiClient.get('bundle_item/', { bundle: bundleId, page_size: 200 })
}
async function createBundleItem(data) {
  return await apiClient.post('bundle_item/', data)
}
async function updateBundleItem(id, data) {
  return await apiClient.patch(`bundle_item/${id}/`, data)
}
async function deleteBundleItem(id) {
  return await apiClient.delete(`bundle_item/${id}/`)
}

async function getSignRequests(params = {}) {
  return await apiClient.get('signing_requests/', params)
}
async function createSignRequest(data) {
  return await apiClient.post('signing_requests/', data)
}
async function getSignRequest(id) {
  return await apiClient.get(`signing_requests/${id}/`)
}
async function deleteSignRequest(id) {
  return await apiClient.delete(`signing_requests/${id}/`)
}
async function cancelSignRequest(id) {
  return await apiClient.post(`signing_requests/${id}/cancel/`)
}

// Public signing endpoints (no auth required — token is the credential)
async function getSigningPage(token) {
  return await apiClient.get(`sign/${token}/`)
}
function getSignPreviewUrl(token) {
  return `${baseURL}sign/${token}/preview/`
}
async function submitSignature(token, signatureData) {
  return await apiClient.post(`sign/${token}/submit/`, { signature_data: signatureData })
}

async function generateContract(projectId) {
  return await apiClient.post(`pdf/generate/contract/${projectId}/`)
}
async function localSignContract(projectId, data) {
  // data: { signer_name, signature_data, send_to_email? }
  return await apiClient.post(`pdf/generate/signed-contract/${projectId}/`, data)
}
async function generateOffer(projectId) {
  return await apiClient.post(`pdf/generate/offer/${projectId}/`)
}
async function generatePickingList(projectId) {
  return await apiClient.post(`pdf/generate/picking-list/${projectId}/`)
}

async function getDocuments(params = {}) {
  return await apiClient.get('documents/', params)
}
async function deleteDocument(id) {
  return await apiClient.delete(`documents/${id}/`)
}
export async function downloadDocument(documentId) {
  return await apiClient.get(`documents/${documentId}/download/`)
}

async function getCustomers(customerId = 0) {
  return customerId != 0
    ? await apiClient.get(`customer/${customerId}/`)
    : await apiClient.get('customer/')
}

async function getWarehouses(params = {}) {
  return await apiClient.get('warehouse/', { page_size: 200, ...params })
}
async function postWarehouse(postData) {
  return await apiClient.post('warehouse/', postData)
}
async function updateWarehouse(id, data) {
  return await apiClient.patch(`warehouse/${id}/`, data)
}
async function deleteWarehouse(id) {
  return await apiClient.delete(`warehouse/${id}/`)
}
async function putItemMetadata(postData) {
  const data = {
    ...postData,
    item: { ...postData.item, type: 'Item' },
    area: { ...postData.area, type: 'Area' }
  }
  return await apiClient.put(`item_metadata/${postData.id}/`, data)
}
async function postItemMetadata(postData) {
  return await apiClient.post('item_metadata/', postData)
}
async function postCategory(postData) {
  return await apiClient.post('category/', postData)
}
async function postCustomer(postData) {
  return await apiClient.post('customer/', postData)
}
async function putCustomer(postData) {
  return await apiClient.put(`customer/${postData.id}/`, postData)
}
async function deleteCustomer(postData) {
  return await apiClient.delete(`customer/${postData.id}/`, postData)
}
async function putCategory(postData) {
  return await apiClient.put(`category/${postData.id}/`, postData)
}
async function postItem(postData) {
  const data = {
    ...postData,
    category:
      typeof postData.category === 'object' && postData.category !== null
        ? postData.category.id
        : postData.category,
    wiki_page: wikiPageToRef(postData.wiki_page)
  }
  return await apiClient.post('item/', data)
}
async function putItem(postData) {
  const data = {
    ...postData,
    category:
      typeof postData.category === 'object' && postData.category !== null
        ? postData.category.id
        : postData.category,
    wiki_page: wikiPageToRef(postData.wiki_page)
  }
  return postData.contract_addition_pdf
    ? await apiClient.patchForm(`item/${postData.id}/`, itemFormData(data))
    : await apiClient.put(`item/${postData.id}/`, data)
}
async function postItemAndMetadata(item, metadata) {
  const itemData = await postItem(item)
  metadata.item = { type: 'Item', id: itemData.id }
  return await postItemMetadata(metadata)
}
async function postCheckout(items, user, expectedCheckin = null) {
  const body = { item_metadata_ids: items, user }
  if (expectedCheckin) body.expected_checkin = expectedCheckin
  return await apiClient.post('checkout_user/', body)
}
async function postCheckoutProject(projectId, item_metadata_ids) {
  return await apiClient.post('checkout_project/', {
    project: parseInt(projectId, 10),
    item_metadata_ids
  })
}

/**
 * Bulk checkout items to an anonymous borrower (guest).
 * Creates a one-time AnonUser record and checks out all items against it.
 * POST /api/checkout_anon_user/
 *
 * @param {Object} anonUserData - { full_name, email?, phone?, organization?, external_id?, notes? }
 * @param {number[]} itemMetadataIds - List of ItemMetadata IDs to check out
 * @param {Object} options - { project?, expectedCheckin?, atomic? }
 */
async function postCheckoutAnonUser(anonUserData, itemMetadataIds, { project = null, expectedCheckin = null, atomic = true } = {}) {
  const body = { anon_user: anonUserData, item_metadata_ids: itemMetadataIds, atomic }
  if (project) body.project = parseInt(project, 10)
  if (expectedCheckin) body.expected_checkin = expectedCheckin
  return await apiClient.post('checkout_anon_user/', body)
}

/**
 * Check out a consumable (type 4) item with a specific quantity.
 * Uses POST /api/item_checkout/ directly instead of the bulk endpoints
 * since quantity is only meaningful for consumables.
 */
async function postConsumableCheckout(item_metadata_id, { userId = null, projectId = null, quantity = 1, expectedCheckin = null } = {}) {
  const body = {
    item_metadata: { type: 'ItemMetadata', id: item_metadata_id },
    quantity: Math.max(1, quantity)
  }
  if (userId) body.user = userId
  if (projectId) body.project = { type: 'Project', id: parseInt(projectId, 10) }
  if (expectedCheckin) body.expected_checkin = expectedCheckin
  return await apiClient.post('item_checkout/', body)
}

async function getSearchResults(query, { limit } = {}) {
  const params = { q: query }
  if (limit != null) params.limit = limit
  return await apiClient.get('search/', params)
}
async function getUserSearchResults(query) {
  return await apiClient.get('user_search/', { q: query })
}

// --- project / picking APIs ---
async function getProjects(params = {}) {
  return await apiClient.get('project/', params)
}

/**
 * Get active project item assignments for a project or checkout.
 * GET /api/project_item_assignment/
 */
async function getProjectItemAssignments(params = {}) {
  return await apiClient.get('project_item_assignment/', params)
}

/**
 * Create a ProjectItemAssignment — assign a person to hold a project-checked-out item.
 * POST /api/project_item_assignment/
 * body: { item_checkout, user } OR { item_checkout, anon_user: { full_name, ... } }
 */
async function createProjectItemAssignment(data) {
  return await apiClient.post('project_item_assignment/', data)
}

/**
 * Field return: mark assignment as returned without touching the ItemCheckout.
 * POST /api/project_item_assignment/{id}/return/
 */
async function returnProjectItemAssignment(id) {
  return await apiClient.post(`project_item_assignment/${id}/return/`, {})
}

/**
 * Get the active ItemCheckout for a specific item on a specific project.
 * Returns the first active checkout or null.
 */
async function getActiveProjectCheckout(itemMetadataId, projectId) {
  const res = await apiClient.get('item_checkout/', {
    item_metadata: itemMetadataId,
    project: projectId,
    is_active: 'true',
    page_size: 1,
  })
  const results = res.results ?? res ?? []
  return results.length ? results[0] : null
}

async function createItemCheckout(data) {
  return await apiClient.post('item_checkout/', data)
}

async function getProject(id) {
  return await apiClient.get(`project/${id}/`)
}
async function createProject(data) {
  return await apiClient.post('project/', data)
}
async function updateProject(id, data) {
  return await apiClient.patch(`project/${id}/`, data)
}
async function deleteProject(id) {
  return await apiClient.delete(`project/${id}/`)
}
async function searchCustomersApi(q) {
  return await apiClient.get('customer/', { name__icontains: q, page_size: 20 })
}

// --- invoice APIs ---
async function getProjectInvoices(projectId) {
  return await apiClient.get('invoice/', { project: projectId })
}
async function generateInvoice(projectId) {
  const today = new Date().toISOString().slice(0, 10)
  return await apiClient.post('invoice/generate/', {
    project_id: projectId,
    project: projectId,
    invoice_date: today
  })
}
async function getInvoice(invoiceId) {
  return await apiClient.get(`invoice/${invoiceId}/`)
}
async function updateInvoice(invoiceId, data) {
  return await apiClient.patch(`invoice/${invoiceId}/`, data)
}
async function getInvoiceExport(invoiceId) {
  return await apiClient.get(`invoice/${invoiceId}/export/`)
}

// --- project section APIs ---
async function getProjectSections(projectId) {
  return await apiClient.get('project_section/', { project: projectId, page_size: 200 })
}
async function createProjectSection(projectId, name) {
  return await apiClient.post('project_section/', { project: parseInt(projectId, 10), name })
}
async function updateProjectSection(id, data) {
  return await apiClient.patch(`project_section/${id}/`, data)
}
async function deleteProjectSection(id, { move_items_to = null } = {}) {
  const url =
    move_items_to != null
      ? `project_section/${id}/?move_items_to=${parseInt(move_items_to, 10)}`
      : `project_section/${id}/`
  return await apiClient.delete(url)
}
async function moveProjectItemToSection(projectItemId, sectionId) {
  return await apiClient.patch(`project_item/${projectItemId}/move-to-section/`, {
    section_id: sectionId ?? null
  })
}

// --- project_item management ---
async function getProjectItems(projectId) {
  return await apiClient.get('project_item/', { project: projectId, page_size: 200 })
}
async function getProjectItemDetail(id) {
  return await apiClient.get(`project_item/${id}/`)
}
async function removeProjectItem(itemId) {
  return await apiClient.post(`project_item/${itemId}/remove_from_project/`, {})
}
async function updateProjectItem(itemId, data) {
  return await apiClient.patch(`project_item/${itemId}/`, data)
}

/** Parse packed_qty:N from project_item.notes (type 3 quantity tracking). */
function parsePackedQtyNotes(notes) {
  if (!notes) return null
  const m = String(notes).match(/^packed_qty:(\d+)$/)
  return m ? parseInt(m[1], 10) : null
}

// ─── private helpers for project-item enrichment ──────────────────────────────

function _getRawMetaId(pi) {
  return typeof pi.item_metadata === 'object' ? pi.item_metadata?.id : pi.item_metadata
}
function _getRawItemId(pi) {
  return typeof pi.item === 'object' ? pi.item?.id : pi.item
}

/**
 * Batch-fetch item_metadata_extended and item records for a flat list of raw
 * project_item objects. Returns { metaLookup, itemLookup } keyed by ID.
 */
async function _buildItemLookups(rawItems) {
  const metaLookup = {}
  const itemLookup = {}

  const uniqueMetaIds = [...new Set(rawItems.map(_getRawMetaId).filter(Boolean))]
  if (uniqueMetaIds.length) {
    const results = await Promise.all(
      uniqueMetaIds.map((id) => apiClient.get(`item_metadata_extended/${id}/`).catch(() => null))
    )
    for (const meta of results) {
      if (meta?.id) metaLookup[meta.id] = meta
    }
  }

  const uniqueItemIds = [
    ...new Set(
      rawItems.filter((pi) => _getRawMetaId(pi) == null).map(_getRawItemId).filter(Boolean)
    )
  ]
  if (uniqueItemIds.length) {
    const results = await Promise.all(
      uniqueItemIds.map((id) => apiClient.get(`item/${id}/`).catch(() => null))
    )
    for (const it of results) {
      if (it?.id) itemLookup[it.id] = it
    }
  }

  return { metaLookup, itemLookup }
}

/**
 * Map raw project_item objects into the enriched shape used by the UI.
 *
 * checkout_status: 'none' | 'checked_out' | 'partially_returned' | 'returned'
 *   none              → 0 active checkouts (not packed yet)
 *   checked_out       → all linked checkouts still active
 *   partially_returned→ some returned, some still active
 *   returned          → all linked checkouts returned
 *
 * packed / packed_quantity are derived from checkout_status + notes (Type 3).
 * For 'partially_returned' on multi-unit items the exact active count is not
 * available on the list endpoint; packed_quantity is approximated as 1 so the
 * slot shows as "in progress" and the detail endpoint's checkouts array is used
 * when the user actually interacts (unpack flow).
 */
function _enrichProjectItems(rawItems, metaLookup, itemLookup) {
  return rawItems.map((pi) => {
    const rawMetaId = _getRawMetaId(pi)
    const rawItemId = _getRawItemId(pi)
    const checkoutStatus = pi.checkout_status ?? 'none'
    const plannedQty = pi.quantity ?? 1

    // Derive packed / packed_quantity from checkout_status ─────────────
    // 'partially_returned': some active, some returned; for multi-unit items
    // the exact active count isn't known from the list endpoint alone.
    // We mark it as at-least-partially-packed (packed_quantity = 1) so it
    // appears in the "remaining" section; the unpack flow uses the detail endpoint.
    function derivePackedType12() {
      switch (checkoutStatus) {
        case 'checked_out':
          return { packed: true, packed_quantity: plannedQty }
        case 'partially_returned':
          return { packed: false, packed_quantity: 1 }
        case 'returned':
        case 'none':
        default:
          return { packed: false, packed_quantity: 0 }
      }
    }

    function derivePackedType3() {
      // Actual count stored in notes; checkout_status tells us if a checkout exists
      const qty = parsePackedQtyNotes(pi.notes) ?? 0
      const active = checkoutStatus === 'checked_out' || checkoutStatus === 'partially_returned'
      return { packed: active && qty > 0, packed_quantity: active ? qty : 0 }
    }

    // Type 4 (Consumable): quantity tracked directly on checkout record
    function derivePackedType4() {
      const qty = parsePackedQtyNotes(pi.notes) ?? plannedQty
      const active = checkoutStatus === 'checked_out' || checkoutStatus === 'partially_returned'
      return { packed: active && qty > 0, packed_quantity: active ? qty : 0 }
    }

    if (rawMetaId != null) {
      // ── Resolved item (specific physical instance) ──────────────────
      const meta =
        metaLookup[rawMetaId] ?? (typeof pi.item_metadata === 'object' ? pi.item_metadata : {})
      const item = meta.item ?? {}
      const itemType = item.item_type
      const { packed, packed_quantity: packedQty } =
        itemType === 3 ? derivePackedType3() :
          itemType === 4 ? derivePackedType4() :
            derivePackedType12()

      return {
        id: pi.id,
        item_metadata_id: rawMetaId,
        item_id: item.id ?? null,
        serial_number: meta.serial_number ?? null,
        asset_tag: meta.assetTag ?? null,
        packed,
        packed_quantity: packedQty,
        planned_quantity: plannedQty,
        checkout_status: checkoutStatus,
        bundle_checkout: pi.bundle_checkout ?? null,
        missing: false,
        unresolved: false,
        section: pi.section ?? null,
        section_name: pi.section_name ?? null,
        price_override: pi.price_override ?? null,
        effective_daily_rate: pi.effective_daily_rate ?? null,
        is_container: item.is_container ?? false,
        children_count: Array.isArray(meta.children) ? meta.children.length : 0,
        item: {
          id: item.id,
          name: item.name ?? meta.assetTag ?? meta.serial_number ?? `#${rawMetaId}`,
          item_type: itemType,
          rental_price_per_day: item.rental_price_per_day ?? null
        }
      }
    } else {
      // ── Unresolved Multi-Item slot (no specific instance committed) ──
      const itemDef = itemLookup[rawItemId] ?? (typeof pi.item === 'object' ? pi.item : {})
      const { packed, packed_quantity: packedQty } = derivePackedType12()

      return {
        id: pi.id,
        item_metadata_id: null,
        item_id: rawItemId,
        serial_number: null,
        asset_tag: null,
        packed,
        packed_quantity: packedQty,
        planned_quantity: plannedQty,
        checkout_status: checkoutStatus,
        bundle_checkout: pi.bundle_checkout ?? null,
        missing: false,
        unresolved: true,
        section: pi.section ?? null,
        section_name: pi.section_name ?? null,
        price_override: pi.price_override ?? null,
        effective_daily_rate: pi.effective_daily_rate ?? null,
        is_container: itemDef.is_container ?? false,
        children_count: 0,
        item: {
          id: rawItemId,
          name: itemDef.name ?? `Item #${rawItemId}`,
          item_type: itemDef.item_type ?? 2,
          rental_price_per_day: itemDef.rental_price_per_day ?? null
        }
      }
    }
  })
}

// ─── public API functions ──────────────────────────────────────────────────────

/**
 * Fetch project details + pick list (flat list, unchanged from original behaviour).
 *   GET /api/project/{id}/
 *   GET /api/project_item/?project={id}
 */
async function getProjectPickList(projectId) {
  const [projectRes, itemsRes] = await Promise.all([
    apiClient.get(`project/${projectId}/`),
    apiClient.get('project_item/', { project: projectId, page_size: 200 })
  ])

  const rawItems = itemsRes.results ?? itemsRes ?? []
  const { metaLookup, itemLookup } = await _buildItemLookups(rawItems)

  return {
    project: projectRes,
    pick_list: _enrichProjectItems(rawItems, metaLookup, itemLookup),
    discount_percentage: projectRes.discount_percentage ?? null,
    effective_usage_days: projectRes.effective_usage_days ?? null
  }
}

/**
 * Fetch project details + grouped project items.
 *   GET /api/project/{id}/
 *   GET /api/project_item/grouped/?project={id}
 *
 * Returns:
 *   {
 *     project,
 *     bundle_groups: [{ id, bundle, expected_checkin, status, all_returned, items: enriched[] }],
 *     ungrouped_list: enriched[],
 *     discount_percentage,
 *     effective_usage_days,
 *   }
 */
async function getProjectItemsGrouped(projectId) {
  const [projectRes, groupedRes] = await Promise.all([
    apiClient.get(`project/${projectId}/`),
    apiClient.get('project_item/grouped/', { project: projectId })
  ])

  const bundleGroups = groupedRes.bundle_groups ?? []
  const ungroupedRaw = groupedRes.ungrouped ?? []

  // Batch-fetch all metadata across every group + ungrouped in one pass
  const allRawItems = [...bundleGroups.flatMap((g) => g.items ?? []), ...ungroupedRaw]
  const { metaLookup, itemLookup } = await _buildItemLookups(allRawItems)

  const enrichedGroups = bundleGroups.map((g) => ({
    ...g,
    items: _enrichProjectItems(g.items ?? [], metaLookup, itemLookup)
  }))

  return {
    project: projectRes,
    bundle_groups: enrichedGroups,
    ungrouped_list: _enrichProjectItems(ungroupedRaw, metaLookup, itemLookup),
    discount_percentage: projectRes.discount_percentage ?? null,
    effective_usage_days: projectRes.effective_usage_days ?? null
  }
}

/**
 * Check out a single item to a project.
 * When project_item_id is supplied, creates a direct item_checkout linked to the
 * pick-list entry (POST /api/item_checkout/) so checkout_status is updated.
 * Falls back to the legacy batch endpoint when no project_item_id is provided.
 */
async function packItem(projectId, { item_metadata_id, project_item_id = null, quantity = 1 }) {
  const count = Math.max(1, quantity)
  if (project_item_id != null) {
    // New: individual checkouts linked to the project_item record
    return await Promise.all(
      Array.from({ length: count }, () =>
        apiClient.post('item_checkout/', {
          item_metadata: { type: 'ItemMetadata', id: item_metadata_id },
          project: { type: 'Project', id: parseInt(projectId, 10) },
          project_item: { type: 'ProjectItem', id: project_item_id }
        })
      )
    )
  }
  // Fallback: legacy batch endpoint (no project_item linkage)
  return await apiClient.post('checkout_project/', {
    project: parseInt(projectId, 10),
    item_metadata_ids: Array.from({ length: count }, () => item_metadata_id)
  })
}

/**
 * Pack a Type-4 (Consumable) item for a project.
 * Creates ONE checkout linked to the project_item entry with the actual quantity field,
 * and stores the count in project_item.notes for display.
 */
async function packConsumableItem(projectId, { project_item_id, item_metadata_id, quantity }) {
  await apiClient.post('item_checkout/', {
    item_metadata: { type: 'ItemMetadata', id: item_metadata_id },
    project: { type: 'Project', id: parseInt(projectId, 10) },
    project_item: { type: 'ProjectItem', id: project_item_id },
    quantity: Math.max(1, quantity)
  })
  await apiClient.patch(`project_item/${project_item_id}/`, { notes: `packed_qty:${quantity}` })
}

/**
 * Pack a Type-3 (quantity) item for the first time.
 * Creates ONE checkout linked to the project_item entry, then stores the actual
 * packed count in project_item.notes.
 */
async function packQtyItem(projectId, { project_item_id, item_metadata_id, quantity }) {
  await apiClient.post('item_checkout/', {
    item_metadata: { type: 'ItemMetadata', id: item_metadata_id },
    project: { type: 'Project', id: parseInt(projectId, 10) },
    project_item: { type: 'ProjectItem', id: project_item_id }
  })
  await apiClient.patch(`project_item/${project_item_id}/`, { notes: `packed_qty:${quantity}` })
}

/**
 * Update the packed count for an already-packed Type-3 item (no checkout changes).
 * PATCH /api/project_item/{id}/
 */
async function updateQtyItemPacked(project_item_id, quantity) {
  await apiClient.patch(`project_item/${project_item_id}/`, {
    notes: quantity > 0 ? `packed_qty:${quantity}` : ''
  })
}

/**
 * Mark a project item as missing — no dedicated API endpoint exists.
 * We PATCH the project_item record with a note so Alex can see it.
 */
async function markItemMissing(projectId, { pick_list_item_id }) {
  return await apiClient.patch(`project_item/${pick_list_item_id}/`, { notes: 'MISSING' })
}

/**
 * Fetch all active checkouts for a project pick list slot, sorted newest-first.
 * Used to populate the UnpackModal with specific instances the user can choose from.
 *
 * Returns an array of:
 *   { id, checkout_date, label, serial_number, asset_tag }
 *
 * Primary path (new system): GET /api/project_item/<id>/ → item.checkouts[] filtered to active.
 * Fallback (old data, no project_item linkage on checkouts):
 *   Resolved:   GET /api/item_checkout/?project={id}&item_metadata={id}&is_active=true
 *   Unresolved: GET /api/item_checkout/?project={id}&is_active=true → filter by item.id
 */
async function getPackedCheckoutsForSlot(projectId, item) {
  let checkouts = []

  // ── Primary: use the project_item detail's checkouts array ──────────────
  if (item.id) {
    const detail = await apiClient.get(`project_item/${item.id}/`).catch(() => null)
    const linked = (detail?.checkouts ?? []).filter((co) => co.is_active)
    if (linked.length > 0) {
      checkouts = linked
    }
  }

  // ── Fallback: query item_checkout directly (old data without project_item FK) ─
  if (!checkouts.length) {
    if (item.item_metadata_id) {
      const res = await apiClient.get('item_checkout/', {
        project: projectId,
        item_metadata: item.item_metadata_id,
        is_active: true,
        page_size: 200
      })
      checkouts = res.results ?? res ?? []
    } else {
      const res = await apiClient.get('item_checkout/', {
        project: projectId,
        is_active: true,
        page_size: 200
      })
      const all = res.results ?? res ?? []
      checkouts = all.filter((co) => {
        const meta = co.item_metadata
        const itemId = meta?.item?.id ?? (typeof meta?.item === 'number' ? meta.item : null)
        return itemId === item.item_id
      })
    }
  }

  // Sort newest checkout_date first
  checkouts.sort((a, b) => new Date(b.checkout_date) - new Date(a.checkout_date))

  return checkouts.map((co) => {
    const meta = co.item_metadata ?? {}
    const sn = meta.serial_number ?? null
    const at = meta.assetTag ?? null
    const name = meta.item?.name ?? item.item?.name ?? i18n.global.t('common.unknown')
    const label = at ?? (sn ? `SN: ${sn}` : `ID: ${co.id}`)
    return {
      id: co.id,
      checkout_date: co.checkout_date,
      label,
      serial_number: sn,
      asset_tag: at,
      item_name: name
    }
  })
}

/**
 * Soft-return a specific item_checkout by id (marks status=returned, preserves record).
 * DELETE /api/item_checkout/{id}/ — always returns 204 No Content.
 * Use POST /api/item_checkout/{id}/mark_returned/ if return_notes are needed.
 */
async function unpackProjectItem(checkoutId) {
  await apiClient.delete(`item_checkout/${checkoutId}/`)
}

/** Extract item_metadata entries from the new grouped search response envelope. */
function parseSearchMetas(searchRes, barcode) {
  const b = barcode?.toLowerCase() ?? ''
  const results = searchRes.results?.item_metadata ?? []
  const match =
    results.find(
      (r) =>
        String(r.object_id) === barcode ||
        (r.display?.subtitle ?? '').toLowerCase() === b ||
        (r.display?.name ?? '').toLowerCase() === b
    ) ??
    results[0] ??
    null
  return match ? { id: match.object_id } : null
}

/**
 * Resolve a barcode/serial/asset-tag to an item_metadata record.
 * Returns the resolved meta (with .item and .id) or null if not found.
 */
async function resolveItemMetadata(barcode) {
  const searchRes = await apiClient.get('search/', { q: barcode })
  const stub = parseSearchMetas(searchRes, barcode)
  if (!stub) return null
  return await getItemInstance(stub.id).catch(() => null)
}

/**
 * Add an ad-hoc item to a project by barcode.
 *   1. Search for item_metadata via /api/search/
 *   2. POST /api/project_item/add-to-project/
 */
async function addAdhocItem(projectId, { barcode, section_id = null }) {
  const searchRes = await apiClient.get('search/', { q: barcode })
  const stub = parseSearchMetas(searchRes, barcode)

  if (!stub) {
    const err = new Error(`"${barcode}" not found in inventory.`)
    err.data = { error: err.message }
    throw err
  }

  const meta = await getItemInstance(stub.id).catch(() => null)

  // AddProjectItemRequest has no 'project' field — project is a query param
  const body = { item_metadata_id: stub.id, quantity: 1 }
  if (section_id != null) body.section_id = parseInt(section_id, 10)
  const res = await apiClient.post(
    `project_item/add-to-project/?project=${parseInt(projectId, 10)}`,
    body
  )

  const label = meta?.assetTag ?? meta?.asset_tag ?? meta?.serial_number ?? `#${stub.id}`
  return {
    pick_list_item: {
      id: res?.id ?? res?.project_item_id,
      item_metadata_id: stub.id,
      serial_number: meta?.serial_number ?? null,
      asset_tag: meta?.assetTag ?? meta?.asset_tag ?? null,
      packed: false,
      packed_quantity: 0,
      planned_quantity: 1,
      missing: false,
      item: {
        id: typeof meta?.item === 'number' ? meta.item : meta?.item?.id,
        name: meta?.item?.name ?? label,
        item_type: typeof meta?.item === 'object' ? meta?.item?.item_type : null
      }
    },
    conflicts: res?.conflicts ?? []
  }
}

/**
 * Add a resolved item (specific physical instance) to a project plan.
 * POST /api/project_item/add-to-project/?project={id}
 */
async function addItemToProjectById(
  projectId,
  item_metadata_id,
  { quantity = 1, section_id = null } = {}
) {
  const body = { item_metadata_id, quantity }
  if (section_id != null) body.section_id = parseInt(section_id, 10)
  return await apiClient.post(
    `project_item/add-to-project/?project=${parseInt(projectId, 10)}`,
    body
  )
}

/**
 * Add an unresolved Multi-Item slot to a project plan.
 * Schedules N units of an item type without committing which specific instances.
 * POST /api/project_item/add-to-project/?project={id}
 */
async function addUnresolvedMultiItemToProject(
  projectId,
  item_id,
  quantity,
  { section_id = null } = {}
) {
  const body = {
    item_id: parseInt(item_id, 10),
    quantity: Math.max(1, parseInt(quantity, 10) || 1)
  }
  if (section_id != null) body.section_id = parseInt(section_id, 10)
  return await apiClient.post(
    `project_item/add-to-project/?project=${parseInt(projectId, 10)}`,
    body
  )
}

async function commitCart(payload) {
  return await apiClient.post('cart_checkout/', payload)
}
async function updateConsumablePackedQuantity(projectItemId, quantity) {
  return await apiClient.post(`project_item/${projectItemId}/packed_quantity/`, { quantity })
}
async function updateBundleVerification(projectId, bundleId, data) {
  return await apiClient.post(`bundle/${bundleId}/verification/?project=${projectId}`, data)
}

// --- bundle APIs ---

/**
 * Read a project-specific bundle checklist, including persisted verification.
 */
async function getBundleDetail(projectId, bundleId) {
  return await apiClient.get(`bundle/${bundleId}/verification/`, { project: projectId })
}

/**
 * Verify all members or save a partial checklist without creating checkouts.
 */
async function verifyBundle(projectId, bundleId, { mode }) {
  return await apiClient.post(`bundle/${bundleId}/verification/?project=${projectId}`, { mode })
}

/**
 * Checkout a bundle to a project.
 * POST /api/bundle_checkout/ { bundle, project }
 */
async function postBundleCheckout(bundleId, { projectId = null, userId = null, anonUser = null, expectedCheckin = null, instanceOverrides = null } = {}) {
  const body = { bundle: parseInt(bundleId, 10) };
  if (projectId) body.project = parseInt(projectId, 10);
  if (userId) body.user = parseInt(userId, 10);
  if (anonUser) body.anon_user = anonUser;
  if (expectedCheckin) body.expected_checkin = expectedCheckin;
  if (instanceOverrides && instanceOverrides.length) body.instance_overrides = instanceOverrides;
  return await apiClient.post('bundle_checkout/', body);
}

/**
 * Return all items in a bundle checkout atomically.
 * POST /api/bundle_checkout/{id}/return/ { notes? }
 */
async function returnBundleCheckout(checkoutId, notes = null) {
  const body = notes ? { notes } : {}
  return await apiClient.post(`bundle_checkout/${checkoutId}/return/`, body)
}

/**
 * Verify one member on a project-specific bundle checklist.
 */
async function verifyBundleItem(projectId, bundleId, data) {
  return await apiClient.post(`bundle/${bundleId}/verification/?project=${projectId}`, { mode: 'item', ...data })
}

// --- returns API ---

/**
 * Resolve a barcode to an item + its active checkout.
 *   1. GET /api/search/?q=<barcode>
 *   2. GET /api/item_checkout/?item_metadata=<id>&is_active=true
 * Returns { item_metadata: { ...meta, project, _checkout_id } }
 */
async function resolveReturn(barcode) {
  const searchRes = await apiClient.get('search/', { q: barcode })
  const stub = parseSearchMetas(searchRes, barcode)

  if (!stub) throw { data: { error: `"${barcode}" not found in inventory.` } }

  const meta = await getItemInstance(stub.id).catch(() => null)
  if (!meta) throw { data: { error: `"${barcode}" not found in inventory.` } }

  const checkoutRes = await apiClient.get('item_checkout/', {
    item_metadata: meta.id,
    is_active: true
  })
  const checkout = (checkoutRes.results ?? checkoutRes)?.[0] ?? null

  return {
    item_metadata: {
      ...meta,
      // Normalize camelCase assetTag → snake_case for view templates
      asset_tag: meta.assetTag ?? meta.asset_tag ?? null,
      display_name: meta.assetTag ?? meta.asset_tag ?? meta.serial_number ?? `#${meta.id}`,
      project: checkout?.project ?? null,
      _checkout_id: checkout?.id ?? null,
      _checkout_quantity: checkout?.quantity ?? null,
    }
  }
}

/**
 * Mark a checkout as returned.
 *   Single item:  resolves active checkout then POST /api/item_checkout/{id}/mark_returned/
 *   Bulk manual:  loops over item_metadata_ids
 *
 * For Consumable (type 4) and Quantity (type 3) items, pass `quantity_returned`
 * to specify how many units are coming back (defaults to full checkout quantity).
 */
async function postReturn(data) {
  const buildBody = (extra = {}) => {
    const body = { condition: data.condition ?? 'good', ...extra }
    if (data.quantity_returned != null) body.quantity_returned = data.quantity_returned
    return body
  }

  // checkout_id already resolved by resolveReturn and stored on the item
  if (data._checkout_id) {
    return await apiClient.post(`item_checkout/${data._checkout_id}/mark_returned/`, buildBody())
  }

  // Fallback: look up active checkout by item_metadata_id
  if (data.item_metadata_id) {
    const checkoutRes = await apiClient.get('item_checkout/', {
      item_metadata: data.item_metadata_id,
      is_active: true
    })
    const checkout = (checkoutRes.results ?? checkoutRes)?.[0]
    if (!checkout) throw { data: { error: i18n.global.t('ui.noActiveCheckout') } }
    return await apiClient.post(`item_checkout/${checkout.id}/mark_returned/`, buildBody())
  }

  // Bulk manual return
  if (data.item_metadata_ids?.length) {
    const results = []
    for (const mid of data.item_metadata_ids) {
      try {
        const checkoutRes = await apiClient.get('item_checkout/', {
          item_metadata: mid,
          is_active: true
        })
        const checkout = (checkoutRes.results ?? checkoutRes)?.[0]
        if (checkout)
          results.push(
            await apiClient.post(`item_checkout/${checkout.id}/mark_returned/`, { condition: 'good' })
          )
      } catch {
        /* skip items without an active checkout */
      }
    }
    return results
  }
}

/**
 * Create a damage/service record.
 * POST /api/service_history/ { item_metadata, title, description, maintenance_type }
 * Note: photo upload would require a separate multipart endpoint; we include it in description for now.
 */
async function postMaintenanceReport({ item_metadata_id, note, photo_base64 }) {
  return await apiClient.post('service_history/', {
    item_metadata: item_metadata_id,
    title: i18n.global.t('ui.damageReport'),
    description: note,
    maintenance_type: 'repair',
    status: 'pending'
  })
}

// --- maintenance schedule / service history ---
async function patchItemMetadataDowntime(id, in_downtime, downtime_reason = '') {
  return await apiClient.patch(`item_metadata/${id}/`, { in_downtime, downtime_reason })
}
async function getMaintenanceSchedules(params = {}) {
  return await apiClient.get('maintenance_schedule/', { page_size: 200, ...params })
}
async function createMaintenanceSchedule(data) {
  return await apiClient.post('maintenance_schedule/', data)
}
async function updateMaintenanceSchedule(id, data) {
  return await apiClient.patch(`maintenance_schedule/${id}/`, data)
}
async function deleteMaintenanceSchedule(id) {
  return await apiClient.delete(`maintenance_schedule/${id}/`)
}
async function markMaintenanceScheduleComplete(id, data = {}) {
  return await apiClient.post(`maintenance_schedule/${id}/mark_complete/`, data)
}
async function getServiceHistory(params = {}) {
  return await apiClient.get('service_history/', { page_size: 200, ...params })
}
async function getServiceHistoryById(id) {
  return await apiClient.get(`service_history/${id}/`)
}
async function createServiceHistory(data) {
  return await apiClient.post('service_history/', data)
}
async function createServiceHistoryWithImage(formData) {
  return await apiClient.postForm('service_history/', formData)
}
async function updateServiceHistory(id, data) {
  return await apiClient.patch(`service_history/${id}/`, data)
}
async function updateServiceHistoryWithImage(id, formData) {
  return await apiClient.patchForm(`service_history/${id}/`, formData)
}
async function deleteServiceHistory(id) {
  return await apiClient.delete(`service_history/${id}/`)
}
async function getMaintenanceScheduleById(id) {
  return await apiClient.get(`maintenance_schedule/${id}/`)
}

// --- tenant user management (admin only) ---
async function adminSetUserPassword(tenantSlug, userId, new_password) {
  return await apiClient.post(`auth/tenant/${tenantSlug}/users/${userId}/password/`, {
    new_password
  })
}
async function adminGetUserRoles(tenantSlug, userId) {
  return await apiClient.get(`auth/tenant/${tenantSlug}/users/${userId}/roles/`)
}
async function adminSetUserRoles(tenantSlug, userId, roles) {
  return await apiClient.post(`auth/tenant/${tenantSlug}/users/${userId}/roles/`, { roles })
}
async function adminSetLocationPermission(tenantSlug, userId, locationId, permission_level) {
  return await apiClient.post(
    `auth/tenant/${tenantSlug}/users/${userId}/locations/${locationId}/permission/`,
    { location_id: locationId, permission_level }
  )
}
async function adminGetUserLocationPermissions(tenantSlug, userId) {
  return await apiClient.get(`auth/tenant/${tenantSlug}/users/${userId}/locations/`)
}
async function getUsers(params = {}) {
  return await apiClient.get('user/', { page_size: 100, ...params })
}
async function getUser(id) {
  return await apiClient.get(`user/${id}/`)
}
async function inviteUser(data) {
  return await apiClient.post('user/invite/', data)
}
async function updateUser(id, data) {
  return await apiClient.patch(`user/${id}/`, data)
}
async function deleteUser(id) {
  return await apiClient.delete(`user/${id}/`)
}
async function removeUserMembership(id) {
  return await apiClient.post(`user/${id}/remove_membership/`)
}

// --- current user (me) ---
async function getMe() {
  return await apiClient.get('auth/me/')
}
async function updateMe(data) {
  return await apiClient.patch('auth/me/', data)
}
async function changeMyPassword(current_password, new_password) {
  return await apiClient.post('auth/me/password/', { current_password, new_password })
}
async function getCurrentTenantRole() {
  return await apiClient.get('auth/tenant/current-role/')
}

async function getPriceGroups(params = {}) {
  return await apiClient.get('price_group/', { page_size: 200, ...params })
}
async function getPriceGroup(id) {
  return await apiClient.get(`price_group/${id}/`)
}
async function createPriceGroup(data) {
  return await apiClient.post('price_group/', data)
}
async function updatePriceGroup(id, data) {
  return await apiClient.patch(`price_group/${id}/`, data)
}
async function deletePriceGroup(id) {
  return await apiClient.delete(`price_group/${id}/`)
}
// --- branding ---
export async function getBranding() { return await apiClient.get('branding/'); }
export async function updateBranding(data) { return await apiClient.patch('branding/', data); }
export async function uploadBrandingLogo(file) {
  const fd = new FormData();
  fd.append('logo', file);
  return await apiClient.patchForm('branding/', fd);
}

async function getPriceGroupItems(params = {}) {
  return await apiClient.get('price_group_item/', { page_size: 200, ...params })
}
async function createPriceGroupItem(data) {
  return await apiClient.post('price_group_item/', data)
}
async function updatePriceGroupItem(id, data) {
  return await apiClient.patch(`price_group_item/${id}/`, data)
}
async function deletePriceGroupItem(id) {
  return await apiClient.delete(`price_group_item/${id}/`)
}

// Inventory transfers
async function getTransfers(params = {}) {
  return await apiClient.get('inventory_transfer/', params)
}
async function getTransfer(id) {
  return await apiClient.get(`inventory_transfer/${id}/`)
}
async function createTransfer(data) {
  return await apiClient.post('inventory_transfer/', data)
}
async function updateTransfer(id, data) {
  return await apiClient.patch(`inventory_transfer/${id}/`, data)
}
async function deleteTransfer(id) {
  return await apiClient.delete(`inventory_transfer/${id}/`)
}
async function addTransferItems(id, item_metadata_ids) {
  return await apiClient.post(`inventory_transfer/${id}/add_items/`, { item_metadata_ids })
}
async function removeTransferItem(id, line_id) {
  return await apiClient.delete(`inventory_transfer/${id}/remove_item/${line_id}/`)
}
async function dispatchTransfer(id) {
  return await apiClient.post(`inventory_transfer/${id}/dispatch/`, {})
}
async function receiveTransfer(id, area = null) {
  return await apiClient.post(`inventory_transfer/${id}/receive/`, area ? { area } : {})
}
async function receiveTransferItem(id, line_id, notes = '', area = null) {
  return await apiClient.post(`inventory_transfer/${id}/receive_item/`, {
    line_id,
    notes,
    ...(area ? { area } : {})
  })
}
async function cancelTransfer(id) {
  return await apiClient.post(`inventory_transfer/${id}/cancel/`, {})
}
async function manualTransfer(data) {
  return await apiClient.post('inventory_transfer/manual_transfer/', data)
}
async function searchItemMetadata(q, extraParams = {}) {
  return await apiClient.get('item_metadata_extended/', {
    search: q,
    page_size: 30,
    ...extraParams
  })
}

// Checkouts overview
async function getActiveCheckouts(params = {}) {
  return await apiClient.get('item_checkout/', { is_active: true, page_size: 200, ...params })
}
async function getAllCheckouts(params = {}) {
  return await apiClient.get('item_checkout/', { page_size: 200, ...params })
}

/**
 * Query active checkouts for a specific item instance that have no expected
 * return date — these signal "open-ended" availability risk.
 * Returns an array of ItemCheckoutDetail objects.
 */
async function getOpenEndedCheckoutsForInstance(item_metadata_id) {
  const res = await apiClient.get('item_checkout/', {
    item_metadata: item_metadata_id,
    is_active: true,
    page_size: 50,
  })
  const all = res.results ?? res ?? []
  return all.filter((c) => !c.effective_expected_checkin)
}

/**
 * Fetch the checkout history for a single item instance (most recent first).
 * Returns an array of ItemCheckoutDetail objects.
 */
async function getCheckoutHistoryForInstance(item_metadata_id, page_size = 8) {
  return await apiClient.get('item_checkout/', {
    item_metadata: item_metadata_id,
    ordering: '-checkout_date',
    page_size,
  })
}

/**
 * Fetch upcoming project plan entries for a specific item instance.
 * Supports pagination: pass { page, pageSize } options.
 * Returns paginated ProjectItem list.
 */
async function getProjectPlansForInstance(item_metadata_id, { page = 1, pageSize = 5, timing = 'upcoming' } = {}) {
  return await apiClient.get('project_item/', {
    item_metadata: item_metadata_id,
    page,
    page_size: pageSize,
    timing,
  })
}

/**
 * Query item availability for a given item ID over a date range.
 * Useful for visualising stock levels and booking windows.
 */
async function getItemAvailability({ item, start, end, show_conflicts = false } = {}) {
  return await apiClient.get('availability/', { item, start, end, show_conflicts })
}
async function markCheckoutReturned(checkoutId, condition = 'good') {
  return await apiClient.post(`item_checkout/${checkoutId}/mark_returned/`, { condition })
}

// Wiki pages
async function getWikiPages(params = {}) {
  return await apiClient.get('wikipage/', { page_size: 200, ...params })
}
async function getWikiPage(id) {
  return await apiClient.get(`wikipage/${id}/`)
}
async function createWikiPage(data) {
  return await apiClient.post('wikipage/', data)
}
async function updateWikiPage(id, data) {
  return await apiClient.patch(`wikipage/${id}/`, data)
}
async function deleteWikiPage(id) {
  return await apiClient.delete(`wikipage/${id}/`)
}

// --- notification APIs ---
async function getNotifications(params = {}) {
  return await apiClient.get('notification/', { page_size: 50, ...params })
}
async function markNotificationRead(id) {
  return await apiClient.post(`notification/${id}/mark_read/`)
}
async function markAllNotificationsRead() {
  return await apiClient.post('notification/mark_all_read/', {})
}
async function getUnreadNotificationCount() {
  return await apiClient.get('notification/unread_count/')
}
async function dismissNotification(id) {
  return await apiClient.delete(`notification/${id}/`)
}
async function clearReadNotifications() {
  return await apiClient.post('notification/clear_read/', {})
}
async function getVapidPublicKey() {
  return await apiClient.get('push-subscription/vapid_public_key/')
}
async function registerPushSubscription(data) {
  return await apiClient.post('push-subscription/', data)
}
async function unregisterPushSubscription(endpoint) {
  return await apiClient.post('push-subscription/unsubscribe/', { endpoint })
}
async function getUserNotificationPreferences() {
  return await apiClient.get('notification-preferences/')
}
async function bulkUpdateNotificationPreferences(preferences) {
  return await apiClient.put('notification-preferences/bulk_update/', preferences)
}
async function updateItemRestockThreshold(item_metadata_id, restock_threshold) {
  return await apiClient.patch(`item_metadata/${item_metadata_id}/`, { restock_threshold })
}

// --- supplier ---
async function getSuppliers(params = {}) {
  return await apiClient.get('supplier/', { page_size: 200, ...params })
}
async function getSupplier(id) {
  return await apiClient.get(`supplier/${id}/`)
}
async function createSupplier(data) {
  return await apiClient.post('supplier/', data)
}
async function updateSupplier(id, data) {
  return await apiClient.patch(`supplier/${id}/`, data)
}
async function deleteSupplier(id) {
  return await apiClient.delete(`supplier/${id}/`)
}

// --- depreciation profiles ---
async function getDepreciationProfiles(params = {}) {
  return await apiClient.get('depreciation_profile/', { page_size: 200, ...params })
}
async function getDepreciationProfile(id) {
  return await apiClient.get(`depreciation_profile/${id}/`)
}
async function createDepreciationProfile(data) {
  return await apiClient.post('depreciation_profile/', data)
}
async function updateDepreciationProfile(id, data) {
  return await apiClient.patch(`depreciation_profile/${id}/`, data)
}
async function deleteDepreciationProfile(id) {
  return await apiClient.delete(`depreciation_profile/${id}/`)
}
async function getTenantSettings() {
  return await apiClient.get('tenant_settings/')
}
async function updateTenantSettings(data) {
  return await apiClient.patch('tenant_settings/', data)
}

// ---------------------------------------------------------------------------
// Translations & i18n overrides
// ---------------------------------------------------------------------------

/**
 * GET /api/translations/
 * Returns all active tenant translation overrides grouped by locale:
 *   { en: { project: "Event", project_plural: "Events" }, nb: { project: "Arrangement" } }
 */
async function getTranslations() {
  return await apiClient.get('translations/')
}

/**
 * GET /api/translation-keys/
 * Returns the whitelisted keys that can be overridden (for admin UI use).
 */
async function getTranslationKeys() {
  return await apiClient.get('translation-keys/')
}

async function getTranslationOverrides(params = {}) {
  return await apiClient.get('translation-overrides/', params)
}

async function createTranslationOverride(data) {
  return await apiClient.post('translation-overrides/', data)
}

async function updateTranslationOverride(id, data) {
  return await apiClient.patch(`translation-overrides/${id}/`, data)
}

async function deleteTranslationOverride(id) {
  return await apiClient.delete(`translation-overrides/${id}/`)
}

async function requestPasswordReset(email) {
  return await apiClient.post('auth/password-reset/', { email })
}

async function confirmPasswordReset(uid, token, new_password) {
  return await apiClient.post('auth/password-reset/confirm/', { uid, token, new_password })
}

async function logOutApi() {
  // Delegate to the auth store which handles token revocation + state clearing
  const authStore = useAuthStore()
  await authStore.logout()
  // Clear per-user UI context so stale data isn't shown after the next login
  try { localStorage.removeItem('location'); localStorage.removeItem('warehouse') } catch { /* ignore */ }
  window.location.href = '/login'
}

function emitGlobalError(message) {
  try {
    window.dispatchEvent(new CustomEvent('global-error', { detail: { message } }))
  } catch (e) {
    /* ignore */
  }
}

// --- macro APIs ---
async function getMacros(params = {}) {
  return await apiClient.get('macro/', params)
}
async function getMacro(id) {
  return await apiClient.get(`macro/${id}/`)
}
async function createMacro(data) {
  return await apiClient.post('macro/', data)
}
async function updateMacro(id, data) {
  return await apiClient.put(`macro/${id}/`, data)
}
async function patchMacro(id, data) {
  return await apiClient.patch(`macro/${id}/`, data)
}
async function deleteMacro(id) {
  return await apiClient.delete(`macro/${id}/`)
}
async function runMacro(id, inputValues = {}) {
  return await apiClient.post(`macro/${id}/run/`, { input_values: inputValues })
}
async function getMacroRun(id) {
  return await apiClient.get(`macro_run/${id}/`)
}
async function getMacroRuns(params = {}) {
  return await apiClient.get('macro_run/', params)
}

/**
 * Extract a human-readable message from a bundle checkout API error.
 * The API may return { error: "...", details: { field: ["msg"] } } or details
 * as a Python-repr string when validation fails.
 */
function parseBundleCheckoutError(e) {
  const base = e?.data?.error ?? i18n.global.t('ui.bundleCheckoutFailed')
  const details = e?.data?.details
  if (!details) return base
  if (typeof details === 'object' && details !== null) {
    const msgs = Object.values(details)
      .flat()
      .filter((m) => typeof m === 'string')
    if (msgs.length) return msgs[0]
  }
  if (typeof details === 'string') {
    const m = details.match(/\["([^"]+)"\]/) ?? details.match(/\['([^']+)'\]/)
    if (m) return m[1]
  }
  return base
}

export {
  parseBundleCheckoutError,
  getProjectSections,
  createProjectSection,
  updateProjectSection,
  deleteProjectSection,
  moveProjectItemToSection,
  getUsers,
  getUser,
  inviteUser,
  updateUser,
  deleteUser,
  removeUserMembership,
  adminSetUserPassword,
  adminGetUserRoles,
  adminSetUserRoles,
  adminSetLocationPermission,
  adminGetUserLocationPermissions,
  getTenantRoles,
  getMe,
  updateMe,
  changeMyPassword,
  getCurrentTenantRole,
  getActiveCheckouts,
  getAllCheckouts,
  getOpenEndedCheckoutsForInstance,
  getCheckoutHistoryForInstance,
  getProjectPlansForInstance,
  markCheckoutReturned,
  getWikiPages,
  getWikiPage,
  createWikiPage,
  updateWikiPage,
  deleteWikiPage,
  getTransfers,
  getTransfer,
  createTransfer,
  updateTransfer,
  deleteTransfer,
  addTransferItems,
  removeTransferItem,
  dispatchTransfer,
  receiveTransfer,
  receiveTransferItem,
  cancelTransfer,
  manualTransfer,
  searchItemMetadata,
  getCustomers,
  postCustomer,
  putCustomer,
  deleteCustomer,
  getCategory,
  putCategory,
  postCategory,
  createCategory,
  updateCategory,
  deleteCategory,
  getUserSearchResults,
  putItemMetadata,
  getStatistics,
  getFrontpage,
  getArea,
  getAreas,
  createArea,
  updateArea,
  deleteArea,
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  getWarehouse,
  getWarehouses,
  postWarehouse,
  updateWarehouse,
  deleteWarehouse,
  getItemMetadata,
  getItems,
  getItemsList,
  getFirstItemMetadataId,
  putItem,
  postItemAndMetadata,
  postItem,
  postItemMetadata,
  postCheckout,
  postCheckoutProject,
  postCheckoutAnonUser,
  postConsumableCheckout,
  getItemMetadataExtended,
  getItemMetadataTimeline,
  getItemFleetTimeline,
  getSearchResults,
  logOutApi,
  authDiscover,
  authLogin,
  authOidcAuthorize,
  authOidcCallback,
  getTenantOidc,
  getTenants,
  switchTenant,
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  searchCustomersApi,
  getProjectItems,
  getProjectItemDetail,
  removeProjectItem,
  updateProjectItem,
  getProjectPickList,
  getProjectItemsGrouped,
  packItem,
  commitCart,
  updateConsumablePackedQuantity,
  updateBundleVerification,
  packQtyItem,
  packConsumableItem,
  updateQtyItemPacked,
  markItemMissing,
  addAdhocItem,
  resolveReturn,
  postReturn,
  postMaintenanceReport,
  getBundleDetail,
  verifyBundle,
  verifyBundleItem,
  postBundleCheckout,
  returnBundleCheckout,
  getItemById,
  createItem,
  deleteItem,
  getItemInstances,
  getChildInstances,
  getItemInstance,
  getItemValueSummary,
  createItemInstance,
  updateItemInstance,
  deleteItemInstance,
  getBundles,
  getBundle,
  createBundle,
  updateBundle,
  deleteBundle,
  getBundleItems,
  createBundleItem,
  updateBundleItem,
  deleteBundleItem,
  addItemToProjectById,
  addUnresolvedMultiItemToProject,
  resolveItemMetadata,
  getPackedCheckoutsForSlot,
  unpackProjectItem,
  generateInvoice,
  getInvoice,
  updateInvoice,
  getInvoiceExport,
  getProjectInvoices,
  getPriceGroups,
  getPriceGroup,
  createPriceGroup,
  updatePriceGroup,
  deletePriceGroup,
  getPriceGroupItems,
  createPriceGroupItem,
  updatePriceGroupItem,
  deletePriceGroupItem,
  patchItemMetadataDowntime,
  getMaintenanceSchedules,
  createMaintenanceSchedule,
  updateMaintenanceSchedule,
  deleteMaintenanceSchedule,
  markMaintenanceScheduleComplete,
  getServiceHistory,
  getServiceHistoryById,
  createServiceHistory,
  createServiceHistoryWithImage,
  getDocuments,
  deleteDocument,
  updateServiceHistory,
  updateServiceHistoryWithImage,
  deleteServiceHistory,
  getMaintenanceScheduleById,
  getSignRequest,
  createSignRequest,
  getSignRequests,
  cancelSignRequest,
  deleteSignRequest,
  getSigningPage,
  getSignPreviewUrl,
  submitSignature,
  generateContract,
  localSignContract,
  generateOffer,
  generatePickingList,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getUnreadNotificationCount,
  dismissNotification,
  clearReadNotifications,
  getVapidPublicKey,
  registerPushSubscription,
  unregisterPushSubscription,
  getUserNotificationPreferences,
  bulkUpdateNotificationPreferences,
  updateItemRestockThreshold,
  getMacros,
  getMacro,
  createMacro,
  updateMacro,
  patchMacro,
  deleteMacro,
  runMacro,
  getMacroRun,
  getMacroRuns,
  getItemAvailability,
  requestPasswordReset,
  confirmPasswordReset,
  getSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  getDepreciationProfiles,
  getDepreciationProfile,
  createDepreciationProfile,
  updateDepreciationProfile,
  deleteDepreciationProfile,
  getTenantSettings,
  updateTenantSettings,
  getTranslations,
  getTranslationKeys,
  getTranslationOverrides,
  createTranslationOverride,
  updateTranslationOverride,
  deleteTranslationOverride,
  getProjectItemAssignments,
  createProjectItemAssignment,
  returnProjectItemAssignment,
  getActiveProjectCheckout,
  createItemCheckout,
  getSystemVersion,
  listImportSessions,
  createImportSession,
  getImportSession,
  getImportSessionRows,
  updateImportSession,
  executeImportSession,
  validateImportSession,
  deleteImportSession,
}

// ---------------------------------------------------------------------------
// Import Sessions
// ---------------------------------------------------------------------------

async function listImportSessions(params = {}) {
  return await apiClient.get('import_sessions/', params)
}

async function createImportSession(formData) {
  return await apiClient.postForm('import_sessions/', formData)
}

async function getImportSession(id) {
  return await apiClient.get(`import_sessions/${id}/`)
}

async function getImportSessionRows(id, page = 1, pageSize = 50, useOriginal = false) {
  return await apiClient.get(`import_sessions/${id}/rows/`, {
    page, page_size: pageSize, ...(useOriginal ? { use_original: 1 } : {}),
  })
}

async function updateImportSession(id, data) {
  return await apiClient.patch(`import_sessions/${id}/`, data)
}

async function executeImportSession(id) {
  return await apiClient.post(`import_sessions/${id}/execute/`)
}

async function validateImportSession(id) {
  return await apiClient.get(`import_sessions/${id}/validate/`)
}

async function deleteImportSession(id) {
  return await apiClient.delete(`import_sessions/${id}/`)
}

export async function saveFrontpageCard(card) {
  const { id, ...payload } = card
  return id ? apiClient.patch(`frontpage/${id}/`, payload) : apiClient.post('frontpage/', payload)
}
export async function deleteFrontpageCard(id) {
  return apiClient.delete(`frontpage/${id}/`)
}
export async function reorderFrontpageCards(ids) {
  return apiClient.post('frontpage/reorder/', { ids })
}
export async function getFrontpageDashboard(from, to) {
  return apiClient.get('frontpage/dashboard/', { from, to })
}
